import React, {useMemo} from 'react'
import Button from "../../Button";
import {ChevronRightIcon, ChevronLeftIcon} from "@heroicons/react/solid";
import {SimpleSelect} from "../../SimpleSelect";
import {formatTimeDifference} from "../../../services/date";
import Toggle from "../../Form/Toggle";
import {Option} from "../../SimpleSelect/SimpleSelect";
import {FeatureCollection, Point} from "geojson";
import {FilteredVisitorMovementFeatureProperties} from "./VisitorsMovementMap.helpers";
import {useOperator} from "../../../context/OperatorContext";

// Types ----

type LocationControlProps = {
  data?: FeatureCollection<Point, FilteredVisitorMovementFeatureProperties>
  visitorOptions: Option[]
  visitorValue: string | number | null
  isLoading?: boolean
  onSelectVisitor: (value: string | number | null) => void
  selectedMovementPoint: number
  onPreviousPoint?: () => void
  onNextPoint?: () => void
  onShowHeatmap: (value: boolean) => void
  showHeatmap?: boolean
}

// Component ----

export function LocationControl({
  data,
  visitorOptions,
  visitorValue,
  isLoading,
  onSelectVisitor,
  selectedMovementPoint,
  onPreviousPoint,
  onNextPoint,
  onShowHeatmap,
  showHeatmap = false,
}: LocationControlProps) {
  const {settings} = useOperator();

  const mapFeatures = useMemo(() => data?.features ?? [], [data]);
  const numberOfFeatures = mapFeatures.length ?? 0;
  const locale = settings?.localisation.locale ?? "en-GB";
  const hasPreviousFeature = !!numberOfFeatures && selectedMovementPoint > 1;
  const hasNextFeature = !!numberOfFeatures && selectedMovementPoint < numberOfFeatures;
  const previousFeature = hasPreviousFeature ? mapFeatures[selectedMovementPoint - 2] : null;
  const currentFeature = mapFeatures[selectedMovementPoint - 1];
  const nextFeature = hasNextFeature ? mapFeatures[selectedMovementPoint] : null;
  const previousFeatureTimeDiff = formatTimeDifference(currentFeature?.properties.occurred_at, previousFeature?.properties.occurred_at);
  const nextFeatureTimeDiff = formatTimeDifference(currentFeature?.properties.occurred_at, nextFeature?.properties.occurred_at);

  return (
    <div className={`flex flex-col gap-5 absolute left-[8px] top-[8px] bg-white rounded-md p-2 shadow-border w-72`}>
      <div className="flex-1 max-w-[48ch]">
        <SimpleSelect
          id='visitor'
          name='visitor'
          label='Visitor'
          onChange={onSelectVisitor}
          value={visitorValue}
          isLoading={isLoading}
          options={visitorOptions}
          defaultOptionText='Choose a visitor'
        />
      </div>

      {numberOfFeatures > 1 &&
        <>
          <div className='flex gap-1 items-center'>
            <Button size={Button.sizes.TINY} color={Button.colors.WHITE} onClick={onPreviousPoint} title='Previous location' disabled={selectedMovementPoint === 1}>
              <ChevronLeftIcon className=' w-5 h-5' />
              <span className='sr-only'>Previous location</span>
            </Button>

            <Button size={Button.sizes.TINY} color={Button.colors.WHITE} onClick={onNextPoint} title='Next location' disabled={!mapFeatures?.length || selectedMovementPoint === mapFeatures?.length}>
              <ChevronRightIcon className=' w-5 h-5' />
              <span className='sr-only'>Next location</span>
            </Button>

            <p className='text-sm text-gray-600 ml-2'>{numberOfFeatures > 0 ? `${selectedMovementPoint} of ${numberOfFeatures.toLocaleString(locale)} locations` : 'No locations'}</p>
          </div>

          <div className='flex flex-col gap-2'>
            <h2 className='text-sm text-gray-600 font-medium'>Location Times</h2>

            <div className='grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm text-gray-500'>
              <h3>Previous</h3>
              <p>{previousFeature ? `${previousFeature?.properties.occurred_at_tz} (-${previousFeatureTimeDiff})` : '---'}</p>

              <h3 className='text-gray-800 font-medium'>Current</h3>
              <p className='text-gray-800 font-medium'>{currentFeature ? currentFeature.properties.occurred_at_tz : '---'}</p>

              <h3>Next</h3>
              <p>{nextFeature ? `${nextFeature?.properties.occurred_at_tz} (+${nextFeatureTimeDiff})` : '---'}</p>
            </div>
          </div>
        </>
      }

      <div className='flex flex-col gap-2'>
        <hr />

        <Toggle
          label='Show heatmap'
          checked={showHeatmap}
          onChange={onShowHeatmap}
        />
      </div>
    </div>
  );
}